import DOMPurify from 'dompurify';

/**
 * Client-side copy of the server's ChatHtmlSanitizer rules (defense in depth):
 * tags p ul ol li strong em a br code; only href survives, and only for https links on
 * allowlisted hosts. Allowed links get rel="noopener noreferrer" target="_blank"; any other
 * link is unwrapped to its text.
 */
export const CHAT_ALLOWED_TAGS = ['p', 'ul', 'ol', 'li', 'strong', 'em', 'a', 'br', 'code'];

/** Same defaults and entry format as the server's ChatBot:AllowedLinkHosts (see ChatLinkPolicy.cs). */
export const DEFAULT_CHAT_LINK_HOSTS = ['localhost', 'lacounty.gov', 'res.cloudinary.com/los-angeles-county-assessor/'];

let purifier: ReturnType<typeof DOMPurify> | null = null;

function getPurifier(): ReturnType<typeof DOMPurify> {
  // A private instance so these settings never leak into other DOMPurify users.
  purifier ??= DOMPurify(window);
  return purifier;
}

/**
 * Returns the URL to render, or null when it isn't allowed. Entries are host suffixes
 * ("lacounty.gov" = that host and subdomains) or host + path prefix
 * ("res.cloudinary.com/los-angeles-county-assessor/" = that exact host, under that path only).
 * A shared-access signature (sig=) is dropped from the query.
 */
export function allowedChatLinkUrl(url: string | null | undefined, hosts: readonly string[]): string | null {
  if (!url) {
    return null;
  }

  let parsed: URL;
  try {
    parsed = new URL(url.trim());
  } catch {
    return null;
  }

  const host = parsed.hostname.toLowerCase();
  const path = parsed.pathname;

  // Leave localhost alone for dev; otherwise enforce https, default port, and no credentials.
  if (host != "localhost") {
    if (parsed.protocol !== 'https:' || parsed.port !== '' || parsed.username !== '' || parsed.password !== '') {
      return null;
    }
  }

  const encodedTraversal = /%2e|%2f|%5c|\\/i.test(path);

  const allowed = hosts
    .map(h => h.trim().replace(/^\./, ''))
    .filter(h => h.length > 0)
    .some(entry => {
      const slash = entry.indexOf('/');
      if (slash < 0) {
        const suffix = entry.toLowerCase();
        return host === suffix || host.endsWith('.' + suffix);
      }
      const prefix = entry.slice(slash).endsWith('/') ? entry.slice(slash) : entry.slice(slash) + '/';
      return !encodedTraversal && host === entry.slice(0, slash).toLowerCase() && path.startsWith(prefix);
    });

  if (!allowed) {
    return null;
  }

  if (/(^|[?&])sig=/i.test(parsed.search)) {
    parsed.search = '';
  }
  return parsed.href;
}

export function isAllowedChatLink(url: string | null | undefined, hosts: readonly string[]): boolean {
  return allowedChatLinkUrl(url, hosts) !== null;
}

export function sanitizeChatHtml(html: string | null | undefined, hosts: readonly string[] = DEFAULT_CHAT_LINK_HOSTS): string {
  if (!html) {
    return '';
  }

  // Same renames as the server (ChatHtmlSanitizer), so the streamed preview matches the final HTML.
  // DOMPurify runs afterwards, so these replacements can't introduce anything unsafe.
  const normalized = html
    .replace(/<(\/?)b(\s[^>]*)?>/gi, '<$1strong>')
    .replace(/<(\/?)i(\s[^>]*)?>/gi, '<$1em>')
    .replace(/<h[1-6](\s[^>]*)?>/gi, '<p><strong>')
    .replace(/<\/h[1-6]\s*>/gi, '</strong></p>');

  const fragment = getPurifier().sanitize(normalized, {
    ALLOWED_TAGS: CHAT_ALLOWED_TAGS,
    ALLOWED_ATTR: ['href'],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    ALLOWED_URI_REGEXP: /^https:/i,
    KEEP_CONTENT: true,
    RETURN_DOM_FRAGMENT: true,
  });

  for (const anchor of Array.from(fragment.querySelectorAll('a'))) {
    const href = allowedChatLinkUrl(anchor.getAttribute('href'), hosts);
    if (href) {
      anchor.setAttribute('href', href);
      anchor.setAttribute('rel', 'noopener noreferrer');
      anchor.setAttribute('target', '_blank');
    } else {
      anchor.replaceWith(...Array.from(anchor.childNodes));
    }
  }

  const container = document.createElement('div');
  container.appendChild(fragment);
  return container.innerHTML;
}
